import { z } from "zod";

// ─────────────────────────────────────────────────────────────────────────────
// GHL custom-field IDs (fetched from the CTR location; DO NOT change without
// re-verifying against /locations/{id}/customFields).
// ─────────────────────────────────────────────────────────────────────────────
export const GHL_CUSTOM_FIELDS = {
  referredByCpaAttorney: "rw3kzZ4Tetl2Tb48Q9ct",       // RADIO   Yes/No
  approxOwed:            "ktlAyBKovpYizTE7JoOc",       // SINGLE_OPTIONS
  unfiledYears:          "9X6rn16dUbtJCMy21Abx",       // TEXT (numeric)
  wagesGarnished:        "08UPo6BekDtNXEoFb3nf",       // RADIO   Yes/No
  audited:               "Tqy73Y0SUpfKKFgon1PL",       // RADIO   Yes/No/I don't know
  threateningLetters:    "MdelvIXwxvyP0UyFeBY9",       // RADIO   Yes/No
  additionalInfo:        "txMl4j9OrvLMgDDZ2AWQ",       // LARGE_TEXT
  hearFromUs:            "ScXJ9bvrTlKz4l2BOket",       // MULTIPLE_OPTIONS
} as const;

// Canonical GHL option values (English) — must match the picklist exactly.
export const APPROX_OWED_OPTIONS = [
  "Less than $10,000",
  "$10,000 - $20,000",
  "$20,000 - $50,000",
  "$50,000 - $100,000",
  "$100,000-$250,000",
  "$250,000-$1,000,000+",
  "Not sure yet...",
] as const;

const yesNo = z.enum(["Yes", "No"]);

export const contactSchema = z.object({
  firstName: z.string().trim().min(1).max(60),
  lastName: z.string().trim().min(1).max(60),
  email: z.string().trim().toLowerCase().email().max(255),
  phone: z
    .string()
    .trim()
    .transform((v) => v.replace(/[^\d+]/g, ""))
    .pipe(z.string().min(7, "Phone must include at least 7 digits").max(20)),

  referredByCpaAttorney: yesNo,
  approxOwed: z.enum(APPROX_OWED_OPTIONS),
  unfiledYears: z
    .union([z.string(), z.number()])
    .transform((v) => String(v).trim())
    .pipe(z.string().regex(/^\d{1,2}$/, "Enter a whole number of years")),
  wagesGarnished: yesNo,
  audited: yesNo,
  threateningLetters: yesNo,
  additionalInfo: z.string().trim().max(4000).optional().default(""),

  sourcePath: z.string().trim().max(500).optional(),
  language: z.enum(["en", "es"]).optional().default("en"),
  utmSource: z.string().trim().max(120).optional().nullable(),
  utmMedium: z.string().trim().max(120).optional().nullable(),
  utmCampaign: z.string().trim().max(120).optional().nullable(),
});

export type CaseReviewSubmission = z.infer<typeof contactSchema>;

export const leadMagnetSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().toLowerCase().email().max(255),
  guideSlug: z.string().trim().max(80).optional().default("7-secrets"),
  sourcePath: z.string().trim().max(500).optional(),
  utmSource: z.string().trim().max(120).optional().nullable(),
  utmMedium: z.string().trim().max(120).optional().nullable(),
  utmCampaign: z.string().trim().max(120).optional().nullable(),
});